import { GridOptions, ColGroupDef, ColDef } from 'ag-grid-community'
import { CollateralBasketDetail } from 'api'
import {
  gridOptionsBase,
  groupingRowColDef,
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  TypedValueGetterParams,
  StatusCellRenderer
} from 'view/components/datagrid'
import { CollateralBasketActionsButton } from './CollateralBasketActionsButton'
import { AdminCollateralBasketActionsButton } from '../admin/collateralBasket/AdminCollateralBasketActionsButton'
import {
  makeNumberFormatter,
  makeDateFormatter,
  makeBooleanFormatter,
  dateQuickFilterTextGetter,
  makeTimestampFormatter
} from 'view/shared/datagrid'
import { getCollateralBasketStatusColor, getCollateralBasketStatusText } from './CollateralBasketUtils'
import { CollateralBasketTypeHelper, TradingTypeHelper } from 'model'
import { Translation, Locale } from 'locale'

export enum CollateralBasketColumnId {
  COLLATERAL_BASKET_CHECKBOX = 'COLLATERAL_BASKET_CHECKBOX',
  COLLATERAL_BASKET_ACTIONS = 'COLLATERAL_BASKET_ACTIONS',
  COLLATERAL_BASKET_STATUS = 'COLLATERAL_BASKET_STATUS',
  COLLATERAL_BASKET_PRODUCT_SECTOR = 'COLLATERAL_BASKET_PRODUCT_SECTOR',
  COLLATERAL_BASKET_PRODUCT = 'COLLATERAL_BASKET_PRODUCT',
  COLLATERAL_BASKET_TYPE = 'COLLATERAL_BASKET_TYPE',
  COLLATERAL_BASKET_ID = 'COLLATERAL_BASKET_ID',
  COLLATERAL_BASKET_NAME = 'COLLATERAL_BASKET_NAME',
  COLLATERAL_BASKET_DESCRIPTION = 'COLLATERAL_BASKET_DESCRIPTION',
  COLLATERAL_BASKET_OWNER = 'COLLATERAL_BASKET_OWNER',
  COLLATERAL_BASKET_CURRENCY = 'COLLATERAL_BASKET_CURRENCY',
  COLLATERAL_BASKET_TRADING_TYPE = 'COLLATERAL_BASKET_TRADING_TYPE',
  COLLATERAL_BASKET_DBV_CLASS = 'COLLATERAL_BASKET_DBV_CLASS',
  COLLATERAL_BASKET_ISIN = 'COLLATERAL_BASKET_ISIN',
  COLLATERAL_BASKET_CUSIP = 'COLLATERAL_BASKET_CUSIP',
  COLLATERAL_BASKET_MIN_CONCENTRATION = 'COLLATERAL_BASKET_MIN_CONCENTRATION',
  COLLATERAL_BASKET_MAX_CONCENTRATION = 'COLLATERAL_BASKET_MAX_CONCENTRATION',
  COLLATERAL_BASKET_MIN_AMOUNT = 'COLLATERAL_BASKET_MIN_AMOUNT',
  COLLATERAL_BASKET_SUBSTITUTION_ALLOWED = 'COLLATERAL_BASKET_SUBSTITUTION_ALLOWED',
  COLLATERAL_BASKET_SUBSTITUTION_NUM = 'COLLATERAL_BASKET_SUBSTITUTION_NUM',
  COLLATERAL_BASKET_CALCULATE_ACCRUED_INTEREST = 'COLLATERAL_BASKET_CALCULATE_ACCRUED_INTEREST',
  COLLATERAL_BASKET_FROM_DATE = 'COLLATERAL_BASKET_FROM_DATE',
  COLLATERAL_BASKET_TO_DATE = 'COLLATERAL_BASKET_TO_DATE',
  COLLATERAL_BASKET_UPDATED_BY = 'COLLATERAL_BASKET_UPDATED_BY',
  COLLATERAL_BASKET_UPDATED_ON_DATE = 'COLLATERAL_BASKET_UPDATED_ON_DATE',
  COLLATERAL_BASKET_CME_BASKET_REFERENCE = 'COLLATERAL_BASKET_CME_BASKET_REFERENCE'
}

export const getCollateralBasketColumnDefs: (isTrading?: boolean) => (ColGroupDef | ColDef)[] = isTrading => {
  const ActionsButton = isTrading ? CollateralBasketActionsButton : AdminCollateralBasketActionsButton
  return [
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_CHECKBOX,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      width: 36,
      maxWidth: 36,
      pinned: 'left',
      lockPinned: true,
      enableRowGroup: false,
      lockPosition: true,
      lockVisible: true,
      filter: false,
      resizable: false,
      sortable: false,
      hide: isTrading
    },
    groupingRowColDef,
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_ACTIONS,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      equals: () => false,
      width: 150,
      minWidth: 150,
      headerComponent: withAllContext(createGroupedActionRenderer(ActionsButton)),
      cellClass: 'action-cell',
      cellRenderer: withAllContext(createActionsRenderer(ActionsButton)),
      pinned: 'left',
      lockPinned: true,
      enableRowGroup: false,
      lockVisible: true,
      filter: false,
      sortable: false
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_STATUS,
      headerName: Locale.translate(Translation.TC_STATUS),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) =>
        params.data && getCollateralBasketStatusText(params.data.collateralBasket.status),
      width: 200,
      filter: 'agSetColumnFilter',
      cellRenderer: StatusCellRenderer,
      cellRendererParams: {
        getStatusColor: (data: CollateralBasketDetail) => getCollateralBasketStatusColor(data.collateralBasket.status)
      },
      equals: () => false,
      lockVisible: true
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_PRODUCT_SECTOR,
      headerName: Locale.translate(Translation.TC_SECTOR),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.productSector?.name,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_PRODUCT,
      headerName: Locale.translate(Translation.TC_PRODUCT),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.product?.name,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_TYPE,
      headerName: Locale.translate(Translation.TC_BASKET_TYPE),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) =>
        params.data?.productSector?.basketType && CollateralBasketTypeHelper.getName(params.data.productSector.basketType),
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_ID,
      headerName: Locale.translate(Translation.TC_BASKET_ID),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.id,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_NAME,
      headerName: Locale.translate(Translation.TC_BASKET_NAME),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.name,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_DESCRIPTION,
      headerName: Locale.translate(Translation.TC_BASKET_DESCRIPTION),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.description,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_OWNER,
      headerName: Locale.translate(Translation.TC_BASKET_OWNER),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.collateralBasketOwner,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_CURRENCY,
      headerName: Locale.translate(Translation.TC_CURRENCY),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.productSector?.basketCurrency,
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_TRADING_TYPE,
      headerName: Locale.translate(Translation.TC_TRADING_TYPE),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) =>
        params.data && TradingTypeHelper.getName(params.data.collateralBasket.tradingType),
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_DBV_CLASS,
      headerName: Locale.translate(Translation.TC_DBV_CLASS),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.dbvClass,
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_ISIN,
      headerName: Locale.translate(Translation.TC_ISIN),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.collateralBasketIsin,
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_CUSIP,
      headerName: Locale.translate(Translation.TC_CUSIP),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.collateralBasketCusip,
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_MIN_CONCENTRATION,
      headerName: Locale.translate(Translation.TC_MINIMUM_CONCENTRATION_LEVEL),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.minConcentration,
      type: 'numericColumn',
      valueFormatter: makeNumberFormatter({ percent: true }),
      width: 120,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_MAX_CONCENTRATION,
      headerName: Locale.translate(Translation.TC_MAXIMUM_CONCENTRATION_LEVEL),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.maxConcentration,
      type: 'numericColumn',
      valueFormatter: makeNumberFormatter({ percent: true }),
      width: 120,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_MIN_AMOUNT,
      headerName: Locale.translate(Translation.TC_MINIMUM_AMOUNT),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.minAmount,
      type: 'numericColumn',
      width: 120,
      filter: 'agNumberColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_SUBSTITUTION_ALLOWED,
      headerName: Locale.translate(Translation.TC_SUBSTITUTION_ALLOWED),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.substitutionAllowed,
      valueFormatter: makeBooleanFormatter({ defaultValue: false }),
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_SUBSTITUTION_NUM,
      headerName: Locale.translate(Translation.TC_SUBSTITUTION_NUM),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.substitutionsNum,
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_CALCULATE_ACCRUED_INTEREST,
      headerName: Locale.translate(Translation.TC_CALCULATE_ACCRUED_INTEREST),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.calculateAccruedInterest,
      valueFormatter: makeBooleanFormatter({ defaultValue: false }),
      width: 120,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_FROM_DATE,
      headerName: Locale.translate(Translation.TC_FROM_DATE),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.basketFromDate,
      width: 120,
      valueFormatter: makeDateFormatter(),
      filter: 'agDateColumnFilter',
      getQuickFilterText: dateQuickFilterTextGetter
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_TO_DATE,
      headerName: Locale.translate(Translation.TC_TO_DATE),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.basketToDate,
      width: 120,
      valueFormatter: makeDateFormatter(),
      filter: 'agDateColumnFilter',
      getQuickFilterText: dateQuickFilterTextGetter
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_UPDATED_BY,
      headerName: Locale.translate(Translation.TC_LAST_UPDATED_BY),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.lastUpdatedBy,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_UPDATED_ON_DATE,
      headerName: Locale.translate(Translation.TC_LAST_UPDATED_AT),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.lastUpdatedAt,
      width: 120,
      valueFormatter: makeTimestampFormatter(),
      filter: 'agDateColumnFilter',
      getQuickFilterText: dateQuickFilterTextGetter
    },
    {
      colId: CollateralBasketColumnId.COLLATERAL_BASKET_CME_BASKET_REFERENCE,
      headerName: Locale.translate(Translation.TC_BASKET_CME_BASKET_REFERENCE),
      valueGetter: (params: TypedValueGetterParams<CollateralBasketDetail>) => params.data?.collateralBasket.cmeBasketReference,
      width: 200,
      filter: 'agSetColumnFilter'
    }
  ]
}

export const CollateralBasketGridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.collateralBasket.id
}
